// Ported from design/project/App15-FinalSeconds.dc.html, then wired for the last 10 seconds of a battle.
import { useEffect, useRef } from "react";
import { battleState } from "../battleView.js";
import { fmtPrice, sparkline } from "../format.js";
import { displayName, useGame, usePlayer, useTicker } from "../game.js";
import { go } from "../router.js";
import { play } from "../sound.js";
import { SoundToggle } from "../SoundToggle.js";
import { fit } from "../fit.js";

export function App15_FinalSeconds() {
  const { battle, mySide, prices, now } = useGame();
  useTicker(100);
  const long = usePlayer(battle?.long);
  const short = usePlayer(battle?.short);
  const left = battle?.endsAt ? battle.endsAt - now() : 0;
  const secs = Math.max(0, Math.ceil(left / 1000));
  // Each second: a tick, harder for 3, 2, 1; a heartbeat instead when it is too close to call.
  const tooClose = battle && battle.startPrice !== null ? Math.abs(battleState(battle, prices[battle.arena], "long").myShare - 50) < 8 : false;
  const lastSec = useRef(0);
  useEffect(() => {
    if (secs > 0 && secs !== lastSec.current) play(tooClose ? "heartbeat" : secs <= 3 ? "lastTick" : "tick");
    lastSec.current = secs;
    if (secs <= 0) go("App16", true);
  }, [secs]);
  if (!battle || !mySide || battle.startPrice === null) return null;
  const st = battleState(battle, prices[battle.arena], "long");
  const recent = st.since.slice(-20);
  const longShare = st.myShare;
  const close = Math.abs(longShare - 50) < 8;
  const leader = longShare > 50 ? displayName(long, "LONG") : displayName(short, "SHORT");
  const up = st.move >= 0;
  return (
    <>
      <style>{`@keyframes ddBeat{0%,100%{transform:scale(1)}15%{transform:scale(1.08)}30%{transform:scale(1)}}
@keyframes ddVig{0%,100%{opacity:.55}50%{opacity:1}}`}</style>
      <div className="screen">
  <div aria-hidden="true" style={{ position: "absolute", inset: "0", boxShadow: "inset 0 0 120px 30px rgba(255,51,102,0.45)", animation: "ddVig 1s infinite", pointerEvents: "none", zIndex: "4" }}></div>
  <div className="orbR" aria-hidden="true" style={{ top: "120px", left: "-45px", width: "480px", height: "480px" }}></div>
  <div className="pad" style={{ height: "100%", paddingBottom: fit(20, 44) }}>
    <div className="top">
      <span className="chip chipR"><span style={{ width: "6px", height: "6px", borderRadius: "50%", background: "#FF3366", animation: "ddPulse .5s infinite" }}></span>FINAL SECONDS</span>
    </div>
    <SoundToggle style={{ position: "absolute", right: "20px", top: "calc(var(--dd-safe-top) + 22px)" }} />

    <div style={{ marginTop: fit(8, 40), display: "flex", flexDirection: "column", alignItems: "center" }}>
      <span className="lbl" style={{ letterSpacing: "0.3em" }}>SETTLING IN</span>
      <span className="mono" style={{ fontWeight: "800", fontSize: fit(96, 132), lineHeight: fit(100, 140), color: "#FF3366", animation: "ddTimer .5s infinite, ddBeat 1s infinite" }}>{String(secs).padStart(2, "0")}</span>
      <span className="muted" style={{ fontSize: "13px" }}>No exits. No changes. Just vibes.</span>
    </div>

    <div className="g" style={{ marginTop: fit(14, 30), borderRadius: "22px", padding: "18px" }}>
      <div className="row">
        <span className="lbl">LIVE PRICE</span>
        <span className="mono muted" style={{ fontSize: "12px" }}>ENTRY {fmtPrice(battle.arena, st.start)}</span>
      </div>
      <div className="row" style={{ marginTop: "6px", alignItems: "baseline" }}>
        <span className="mono" style={{ fontWeight: "800", fontSize: "34px" }}>{fmtPrice(battle.arena, st.last)}</span>
        <span className={`mono ${up ? "lime" : "red"}`} style={{ fontWeight: "700", fontSize: "14px" }}>{up ? "+" : "-"}{fmtPrice(battle.arena, Math.abs(st.move))}</span>
      </div>
      <svg width="100%" height="56" viewBox="0 0 318 56" preserveAspectRatio="none" style={{ display: "block", marginTop: "10px" }} aria-hidden="true">
        <polyline points={sparkline(recent, 318, 56, 6)} fill="none" stroke={up ? "#CCFF00" : "#FF3366"} strokeWidth="2.5" strokeLinejoin="round"></polyline>
      </svg>
    </div>

    <div style={{ marginTop: "18px", display: "flex", flexDirection: "column", gap: "8px" }}>
      <div className="row" style={{ fontSize: "12px", fontWeight: "700" }}><span className="lime">{displayName(long)} LONG</span><span className="red">SHORT {displayName(short)}</span></div>
      <div style={{ height: "14px", borderRadius: "14px", overflow: "hidden", display: "flex", background: "#161B22" }}>
        <div style={{ width: `${longShare}%`, transition: "width 0.4s", background: "linear-gradient(90deg, #8FB300, #CCFF00)", boxShadow: "0 0 14px rgba(204,255,0,0.6)" }}></div>
        <div style={{ width: "2px", background: "#0B0E14" }}></div>
        <div style={{ flex: "1", background: "linear-gradient(90deg, #FF3366, #B3123F)" }}></div>
      </div>
      {/* A long name shortens with "..." so "is ahead" always shows and nothing runs off the screen. */}
      <span className="syne" style={{ display: "flex", justifyContent: "center", gap: "0.3em", maxWidth: "100%", fontWeight: "800", fontSize: "16px", marginTop: "4px", whiteSpace: "nowrap" }}>
        {close ? "Too close to call" : <><span style={{ minWidth: "0", overflow: "hidden", textOverflow: "ellipsis" }}>{leader}</span><span style={{ flex: "none" }}>is ahead</span></>}
      </span>
    </div>

    <div style={{ marginTop: "auto", display: "grid", gridTemplateColumns: "repeat(4, minmax(0, 1fr))", gap: "8px" }}>
      <button className="g" style={{ height: "48px", borderRadius: "14px", color: "#FFFFFF", fontFamily: "Inter, sans-serif", fontWeight: "700", fontSize: "13px", cursor: "pointer" }}>GG</button>
      <button className="g" style={{ height: "48px", borderRadius: "14px", color: "#FFFFFF", fontFamily: "Inter, sans-serif", fontWeight: "700", fontSize: "13px", cursor: "pointer" }}>Cope</button>
      <button className="g" style={{ height: "48px", borderRadius: "14px", color: "#FFFFFF", fontFamily: "Inter, sans-serif", fontWeight: "700", fontSize: "13px", cursor: "pointer" }}>Send it</button>
      <button className="g" style={{ height: "48px", borderRadius: "14px", color: "#FFFFFF", fontFamily: "Inter, sans-serif", fontWeight: "700", fontSize: "13px", cursor: "pointer" }}>Pray</button>
    </div>
  </div>
</div>
    </>
  );
}
